import React, { useEffect, useMemo, useState } from 'react';
import {
  Building2,
  School,
  Users,
  UserPlus,
  Shield,
  Trophy,
  TrendingUp,
  BarChart3,
  Plus,
  Search
} from 'lucide-react';
import {
  addDoc,
  collection,
  doc,
  getDocs,
  query,
  serverTimestamp,
  updateDoc,
  where,
  writeBatch
} from 'firebase/firestore';
import { db } from '../../config/firebase';
import { leitnerService } from '../../services/leitnerService';
import { useAuth } from '../../contexts/AuthContext';

const ORG_COLLECTION = 'pb-organisations';
const LEARNERS_COLLECTION = 'pb-learners';
const USERS_COLLECTION = 'pb-users';

const ROLE_GROUPS = {
  officials: ['official', 'district_official', 'district-official'],
  educators: ['educator', 'teacher'],
  tutors: ['tutor'],
  learners: ['learner', 'student']
};

const ORG_TYPES = [
  { value: 'district', label: 'District', icon: Building2 },
  { value: 'school', label: 'School', icon: School },
  { value: 'project', label: 'Project', icon: Shield }
];

const getUserRole = (user) => user?.affiliation?.role || user?.role || 'user';
const getDisplayName = (user) => user?.displayName || user?.email || user?.id || 'Unknown';

const isRoleIn = (role, list) => list.includes((role || '').toLowerCase());

const getAffiliationValue = (user, key, fallback = '') =>
  user?.affiliation?.[key] || user?.[key] || fallback;

const LeitnerEntity = ({ bookId }) => {
  const { user, userProfile } = useAuth();
  const [activeSection, setActiveSection] = useState('organisations');
  const [orgs, setOrgs] = useState([]);
  const [users, setUsers] = useState([]);
  const [learners, setLearners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [orgFormOpen, setOrgFormOpen] = useState(false);
  const [orgForm, setOrgForm] = useState({
    name: '',
    type: 'school',
    district: '',
    description: ''
  });
  const [userSearch, setUserSearch] = useState('');
  const [bulkImportOpen, setBulkImportOpen] = useState(false);
  const [bulkImportText, setBulkImportText] = useState('');

  const userRole = getUserRole(userProfile);
  const isDistrictOfficial = isRoleIn(userRole, ROLE_GROUPS.officials);
  const canManageLearners = isRoleIn(userRole, [...ROLE_GROUPS.educators, ...ROLE_GROUPS.tutors]);
  const canEditOrganisations = !isDistrictOfficial;

  useEffect(() => {
    if (!bookId) return;
    loadAll();
  }, [bookId]);

  const loadAll = async () => {
    setLoading(true);
    try {
      await Promise.all([loadOrganisations(), loadUsers(), loadLearners()]);
    } catch (error) {
      console.error('Failed to load entities:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadOrganisations = async () => {
    const snapshot = await getDocs(collection(db, ORG_COLLECTION));
    setOrgs(snapshot.docs.map(docSnap => ({ id: docSnap.id, ...docSnap.data() })));
  };

  const loadUsers = async () => {
    try {
      const usersRef = collection(db, USERS_COLLECTION);
      const q = query(
        usersRef,
        where('affiliation.role', 'in', [
          ...ROLE_GROUPS.officials,
          ...ROLE_GROUPS.educators,
          ...ROLE_GROUPS.tutors,
          ...ROLE_GROUPS.learners
        ])
      );
      const snapshot = await getDocs(q);
      setUsers(snapshot.docs.map(docSnap => ({ id: docSnap.id, ...docSnap.data() })));
    } catch (error) {
      console.error('Failed to load users:', error);
      setUsers([]);
    }
  };

  const loadLearners = async () => {
    const snapshot = await getDocs(collection(db, LEARNERS_COLLECTION));
    setLearners(snapshot.docs.map(docSnap => ({ id: docSnap.id, ...docSnap.data() })));
  };

  const handleCreateOrganisation = async () => {
    if (!orgForm.name.trim()) return;
    const payload = {
      name: orgForm.name.trim(),
      type: orgForm.type,
      district: orgForm.type === 'district' ? null : orgForm.district || null,
      description: orgForm.description?.trim() || null,
      createdBy: user?.uid || null,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };
    await addDoc(collection(db, ORG_COLLECTION), payload);
    setOrgForm({ name: '', type: 'school', district: '', description: '' });
    setOrgFormOpen(false);
    loadOrganisations();
  };

  const handleSyncLearners = async () => {
    setSyncing(true);
    try {
      const learnerUsers = users.filter((u) => {
        const role = getUserRole(u);
        const affiliation = u.affiliation || {};
        return isRoleIn(role, ROLE_GROUPS.learners) && affiliation.type === 'project';
      });
      const existing = new Set(learners.map(l => l.userId || l.id));
      const batch = writeBatch(db);
      let writes = 0;

      learnerUsers.forEach((learner) => {
        const learnerId = learner.id;
        if (existing.has(learnerId)) return;
        batch.set(doc(db, LEARNERS_COLLECTION, learnerId), {
          userId: learnerId,
          displayName: learner.displayName || learner.email || 'Learner',
          email: learner.email || null,
          affiliation: learner.affiliation || null,
          circuit: learner.affiliation?.circuit || null,
          district: learner.affiliation?.district || null,
          grades: learner.affiliation?.grades || [],
          institutionType: learner.affiliation?.institutionType || null,
          name: learner.affiliation?.name || null,
          role: learner.affiliation?.role || 'learner',
          school: learner.affiliation?.school || null,
          subjects: learner.affiliation?.subjects || [],
          type: learner.affiliation?.type || 'project',
          managerIds: [],
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
        writes += 1;
      });

      if (writes > 0) {
        await batch.commit();
        await loadLearners();
      }
    } catch (error) {
      console.error('Failed to sync learners:', error);
    } finally {
      setSyncing(false);
    }
  };

  const handleBulkImport = async () => {
    const lines = bulkImportText
      .split(/\r?\n/)
      .map(line => line.trim())
      .filter(Boolean);
    if (lines.length === 0) return;

    try {
      const batch = writeBatch(db);
      let writes = 0;

      lines.forEach((line) => {
        const [displayNameRaw, emailRaw] = line.split(',').map(part => part.trim());
        const displayName = displayNameRaw || 'Learner';
        const email = emailRaw || null;
        const ref = doc(collection(db, LEARNERS_COLLECTION));
        batch.set(ref, {
          userId: null,
          displayName,
          email,
          affiliation: {
            role: 'learner',
            type: 'project',
            name: '',
            district: '',
            circuit: '',
            school: '',
            grades: [],
            subjects: []
          },
          managerIds: user?.uid ? [user.uid] : [],
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
        writes += 1;
      });

      if (writes > 0) {
        await batch.commit();
        setBulkImportText('');
        setBulkImportOpen(false);
        await loadLearners();
      }
    } catch (error) {
      console.error('Failed to bulk import learners:', error);
    }
  };

  const handleAssignLearner = async (learnerId) => {
    if (!user?.uid) return;
    const learner = learners.find(l => l.id === learnerId);
    if (!learner) return;
    const managerIds = Array.from(new Set([...(learner.managerIds || []), user.uid]));
    await updateDoc(doc(db, LEARNERS_COLLECTION, learnerId), {
      managerIds,
      updatedAt: serverTimestamp()
    });
    await loadLearners();
  };

  const visibleOrgs = useMemo(() => {
    if (!isDistrictOfficial) return orgs;
    const districtName = getAffiliationValue(userProfile, 'district');
    if (!districtName) return [];
    return orgs.filter(org => {
      if (org.type === 'district') return org.name === districtName;
      if (org.district) return org.district === districtName;
      if (org.parentDistrict) return org.parentDistrict === districtName;
      return false;
    });
  }, [orgs, isDistrictOfficial, userProfile]);

  const usersByRole = useMemo(() => {
    const buckets = { officials: [], educators: [], tutors: [], learners: [] };
    users.forEach((u) => {
      const role = getUserRole(u).toLowerCase();
      if (isRoleIn(role, ROLE_GROUPS.officials)) buckets.officials.push(u);
      else if (isRoleIn(role, ROLE_GROUPS.educators)) buckets.educators.push(u);
      else if (isRoleIn(role, ROLE_GROUPS.tutors)) buckets.tutors.push(u);
      else if (isRoleIn(role, ROLE_GROUPS.learners)) buckets.learners.push(u);
    });
    return buckets;
  }, [users]);

  const learnerStats = useLearnerStats(bookId, users);
  const orgStats = useOrgStats(visibleOrgs, learnerStats);

  const filteredUsers = useMemo(() => {
    const queryText = userSearch.trim().toLowerCase();
    if (!queryText) return users;
    return users.filter(u => {
      const name = getDisplayName(u).toLowerCase();
      const email = (u.email || '').toLowerCase();
      const district = getAffiliationValue(u, 'district').toLowerCase();
      const school = getAffiliationValue(u, 'school').toLowerCase();
      return name.includes(queryText) || email.includes(queryText) || district.includes(queryText) || school.includes(queryText);
    });
  }, [users, userSearch]);

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Leitner Entity Manager</h3>
          <p className="text-sm text-gray-500">
            Manage organisations, educators, and learner records with district-aware permissions.
          </p>
        </div>
        {isDistrictOfficial && (
          <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-amber-50 text-amber-700 rounded-full text-xs font-medium">
            <Shield className="h-4 w-4" />
            District official access (view-only)
          </span>
        )}
      </div>

      <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
        <div className="flex border-b">
          <button
            onClick={() => setActiveSection('organisations')}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeSection === 'organisations'
                ? 'border-indigo-600 text-indigo-600'
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            Organisations
          </button>
          <button
            onClick={() => setActiveSection('users')}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeSection === 'users'
                ? 'border-indigo-600 text-indigo-600'
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            Users & Learners
          </button>
        </div>

        {loading ? (
          <div className="p-10 text-center text-gray-500">Loading entities...</div>
        ) : activeSection === 'organisations' ? (
          <div className="p-6 space-y-6">
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
              <div>
                <h4 className="font-semibold text-gray-900">Organisation Overview</h4>
                <p className="text-sm text-gray-500">District, school, and project performance summaries.</p>
              </div>
              {canEditOrganisations && (
                <button
                  onClick={() => setOrgFormOpen(prev => !prev)}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition"
                >
                  <Plus className="h-4 w-4" />
                  Add Organisation
                </button>
              )}
            </div>

            {orgFormOpen && canEditOrganisations && (
              <div className="bg-indigo-50 border border-indigo-100 rounded-xl p-4 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <input
                    type="text"
                    value={orgForm.name}
                    onChange={(e) => setOrgForm(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Organisation name"
                    className="px-3 py-2 border rounded-lg text-sm"
                  />
                  <select
                    value={orgForm.type}
                    onChange={(e) => setOrgForm(prev => ({ ...prev, type: e.target.value }))}
                    className="px-3 py-2 border rounded-lg text-sm"
                  >
                    {ORG_TYPES.map(type => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                  {orgForm.type !== 'district' && (
                    <input
                      type="text"
                      value={orgForm.district}
                      onChange={(e) => setOrgForm(prev => ({ ...prev, district: e.target.value }))}
                      placeholder="District"
                      className="px-3 py-2 border rounded-lg text-sm"
                    />
                  )}
                </div>
                <textarea
                  value={orgForm.description}
                  onChange={(e) => setOrgForm(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Description (optional)"
                  className="w-full px-3 py-2 border rounded-lg text-sm"
                  rows={2}
                />
                <div className="flex justify-end gap-2">
                  <button
                    onClick={() => setOrgFormOpen(false)}
                    className="px-3 py-2 text-sm text-gray-600 hover:text-gray-900"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleCreateOrganisation}
                    className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                  >
                    Save Organisation
                  </button>
                </div>
              </div>
            )}

            {visibleOrgs.length === 0 ? (
              <div className="text-center py-10 text-gray-500 bg-gray-50 rounded-lg">
                No organisations found yet.
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {visibleOrgs.map(org => {
                  const stats = orgStats[org.id];
                  const OrgIcon = ORG_TYPES.find(type => type.value === org.type)?.icon || Building2;
                  return (
                    <div key={org.id} className="border rounded-xl p-4 bg-white shadow-sm">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="p-2 rounded-lg bg-indigo-50 text-indigo-600">
                              <OrgIcon className="h-4 w-4" />
                            </span>
                            <div>
                              <h5 className="font-semibold text-gray-900">{org.name}</h5>
                              <p className="text-xs text-gray-500 capitalize">{org.type}</p>
                            </div>
                          </div>
                          {org.district && (
                            <p className="text-xs text-gray-500 mt-2">District: {org.district}</p>
                          )}
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-gray-500">Learners</p>
                          <p className="text-lg font-semibold text-gray-900">{stats?.learnersCount || 0}</p>
                        </div>
                      </div>

                      {stats ? (
                        <div className="mt-4 grid grid-cols-3 gap-3 text-center">
                          <div className="bg-gray-50 rounded-lg p-2">
                            <p className="text-xs text-gray-500">Attempts</p>
                            <p className="text-sm font-semibold text-gray-900">{stats.totalAttempts}</p>
                          </div>
                          <div className="bg-gray-50 rounded-lg p-2">
                            <p className="text-xs text-gray-500">Accuracy</p>
                            <p className="text-sm font-semibold text-emerald-600">{stats.accuracy}%</p>
                          </div>
                          <div className="bg-gray-50 rounded-lg p-2">
                            <p className="text-xs text-gray-500">Improvement</p>
                            <p className={`text-sm font-semibold ${stats.improvement >= 0 ? 'text-indigo-600' : 'text-red-500'}`}>
                              {stats.improvement >= 0 ? '+' : ''}{stats.improvement}%
                            </p>
                          </div>
                        </div>
                      ) : (
                        <p className="mt-4 text-xs text-gray-500">No learner activity tracked yet.</p>
                      )}

                      {stats && (
                        <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
                          <Leaderboard title="Most Engaged" icon={BarChart3} entries={stats.leaderboards.engaged} />
                          <Leaderboard title="Best Performers" icon={Trophy} entries={stats.leaderboards.performers} />
                          <Leaderboard title="Improving Fastest" icon={TrendingUp} entries={stats.leaderboards.improvers} />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ) : (
          <div className="p-6 space-y-6">
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
              <div>
                <h4 className="font-semibold text-gray-900">Educators & Learners</h4>
                <p className="text-sm text-gray-500">Manage learner records and educator teams.</p>
              </div>
              <div className="relative w-full lg:w-72">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                  placeholder="Search users..."
                  className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <RoleCard title="District Officials" icon={Shield} users={filteredUsers.filter(u => isRoleIn(getUserRole(u), ROLE_GROUPS.officials))} />
              <RoleCard title="Educators" icon={Users} users={filteredUsers.filter(u => isRoleIn(getUserRole(u), ROLE_GROUPS.educators))} />
              <RoleCard title="Tutors" icon={Users} users={filteredUsers.filter(u => isRoleIn(getUserRole(u), ROLE_GROUPS.tutors))} />
            </div>

            <div className="bg-gray-50 rounded-xl border p-4 space-y-4">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                <div>
                  <h5 className="font-semibold text-gray-900 flex items-center gap-2">
                    <Users className="h-4 w-4 text-indigo-600" />
                    Learners
                  </h5>
                  <p className="text-xs text-gray-500">
                    Learner records are synchronized from pb-users and managed in pb-learners.
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={handleSyncLearners}
                    disabled={syncing}
                    className="px-3 py-2 text-xs bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                  >
                    {syncing ? 'Syncing...' : 'Sync Learners'}
                  </button>
                  {canManageLearners && (
                    <button
                      onClick={() => setBulkImportOpen(prev => !prev)}
                      className="px-3 py-2 text-xs border border-indigo-200 text-indigo-600 rounded-lg hover:bg-indigo-50"
                    >
                      Bulk Import
                    </button>
                  )}
                </div>
              </div>

              {bulkImportOpen && canManageLearners && (
                <div className="bg-white border rounded-lg p-3 space-y-3">
                  <div className="text-xs text-gray-500">
                    Paste one learner per line. Format: `Name, email` (email optional).
                  </div>
                  <textarea
                    value={bulkImportText}
                    onChange={(e) => setBulkImportText(e.target.value)}
                    rows={4}
                    className="w-full border rounded-lg px-3 py-2 text-sm"
                    placeholder="Jane Doe, jane@example.com"
                  />
                  <div className="flex justify-end gap-2">
                    <button
                      onClick={() => setBulkImportOpen(false)}
                      className="text-xs text-gray-500 hover:text-gray-700"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleBulkImport}
                      className="px-3 py-2 text-xs bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                    >
                      Import Learners
                    </button>
                  </div>
                </div>
              )}

              <div className="space-y-2">
                {learners.length === 0 ? (
                  <div className="text-center text-gray-500 py-6">No learners synced yet.</div>
                ) : (
                  learners.map((learner) => {
                    const profile = users.find(u => u.id === learner.userId);
                    const displayName = getDisplayName(profile || learner);
                    const district = getAffiliationValue(profile || learner, 'district', 'Unassigned');
                    const school = getAffiliationValue(profile || learner, 'school', 'Unassigned');
                    const isManaged = (learner.managerIds || []).includes(user?.uid);
                    return (
                      <div key={learner.id} className="flex flex-col md:flex-row md:items-center justify-between gap-3 bg-white border rounded-lg p-3">
                        <div>
                          <p className="text-sm font-medium text-gray-900">{displayName}</p>
                          <p className="text-xs text-gray-500">{district} • {school}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          {canManageLearners && (
                            <button
                              onClick={() => handleAssignLearner(learner.id)}
                              disabled={isManaged}
                              className="inline-flex items-center gap-1 px-2 py-1.5 text-xs rounded-lg border border-indigo-200 text-indigo-600 hover:bg-indigo-50 disabled:opacity-50"
                            >
                              <UserPlus className="h-3.5 w-3.5" />
                              {isManaged ? 'Assigned' : 'Assign to me'}
                            </button>
                          )}
                          {(learner.managerIds || []).length > 0 && (
                            <span className="text-xs text-gray-500">
                              Managers: {learner.managerIds.length}
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const RoleCard = ({ title, icon: Icon, users }) => (
  <div className="bg-white border rounded-xl p-4 shadow-sm">
    <div className="flex items-center justify-between mb-3">
      <div className="flex items-center gap-2">
        <span className="p-2 rounded-lg bg-indigo-50 text-indigo-600">
          <Icon className="h-4 w-4" />
        </span>
        <div>
          <p className="font-semibold text-gray-900">{title}</p>
          <p className="text-xs text-gray-500">{users.length} people</p>
        </div>
      </div>
    </div>
    <div className="space-y-2 max-h-48 overflow-y-auto">
      {users.length === 0 ? (
        <p className="text-xs text-gray-500">No users found.</p>
      ) : (
        users.slice(0, 8).map(user => (
          <div key={user.id} className="text-sm text-gray-700">
            {getDisplayName(user)}
            <span className="text-xs text-gray-400"> · {getAffiliationValue(user, 'district') || 'No district'}</span>
          </div>
        ))
      )}
    </div>
  </div>
);

const Leaderboard = ({ title, icon: Icon, entries }) => (
  <div className="bg-gray-50 rounded-lg p-3">
    <div className="flex items-center gap-2 text-xs font-semibold text-gray-700 mb-2">
      <Icon className="h-3.5 w-3.5" />
      {title}
    </div>
    {entries.length === 0 ? (
      <p className="text-xs text-gray-400">No data yet.</p>
    ) : (
      <div className="space-y-1">
        {entries.map((entry) => (
          <div key={entry.userId} className="flex items-center justify-between text-xs text-gray-600">
            <span className="truncate">{entry.name}</span>
            <span className="font-medium text-gray-900">{entry.value}</span>
          </div>
        ))}
      </div>
    )}
  </div>
);

const useLearnerStats = (bookId, users) => {
  const [stats, setStats] = useState([]);

  useEffect(() => {
    if (!bookId) return;
    let mounted = true;
    const load = async () => {
      try {
        const progress = await leitnerService.getAllLearnersProgress(bookId);
        const userMap = new Map(users.map(u => [u.id, u]));
        const now = new Date();
        const recentCutoff = new Date(now);
        recentCutoff.setDate(now.getDate() - 7);

        const computed = progress.map((learner) => {
          const profile = userMap.get(learner.userId);
          const items = [...(learner.questions || []), ...(learner.subquestions || [])].map((item) => {
            const lastReviewed = item.lastReviewed?.toDate
              ? item.lastReviewed.toDate()
              : item.lastReviewed
                ? new Date(item.lastReviewed)
                : null;
            return {
              attempts: item.attempts || 0,
              correctAttempts: item.correctAttempts || 0,
              lastReviewed
            };
          });

          const totalAttempts = items.reduce((sum, item) => sum + item.attempts, 0);
          const totalCorrect = items.reduce((sum, item) => sum + item.correctAttempts, 0);
          const accuracy = totalAttempts > 0 ? Math.round((totalCorrect / totalAttempts) * 100) : 0;

          const recentItems = items.filter(item => item.lastReviewed && item.lastReviewed >= recentCutoff);
          const recentAttempts = recentItems.reduce((sum, item) => sum + item.attempts, 0);
          const recentCorrect = recentItems.reduce((sum, item) => sum + item.correctAttempts, 0);
          const recentAccuracy = recentAttempts > 0 ? Math.round((recentCorrect / recentAttempts) * 100) : 0;

          const improvement = recentAccuracy - accuracy;

          return {
            userId: learner.userId,
            name: getDisplayName(profile || {}),
            affiliation: profile?.affiliation || {},
            totalAttempts,
            accuracy,
            improvement
          };
        });

        if (mounted) setStats(computed);
      } catch (error) {
        console.error('Failed to load learner stats:', error);
      }
    };
    load();
    return () => {
      mounted = false;
    };
  }, [bookId, users]);

  return stats;
};

const useOrgStats = (orgs, learnerStats) => {
  return useMemo(() => {
    const byOrgId = {};
    orgs.forEach((org) => {
      const learners = learnerStats.filter((learner) => {
        const district = learner.affiliation?.district;
        const school = learner.affiliation?.school;
        if (org.type === 'district') return district === org.name;
        if (org.type === 'school') return school === org.name || org.school === school;
        if (org.type === 'project') {
          const projectName = learner.affiliation?.project || learner.affiliation?.name;
          return projectName === org.name;
        }
        return false;
      });

      const totalAttempts = learners.reduce((sum, l) => sum + l.totalAttempts, 0);
      const avgAccuracy = learners.length > 0
        ? Math.round(learners.reduce((sum, l) => sum + l.accuracy, 0) / learners.length)
        : 0;
      const avgImprovement = learners.length > 0
        ? Math.round(learners.reduce((sum, l) => sum + l.improvement, 0) / learners.length)
        : 0;

      const leaderboards = {
        engaged: [...learners].sort((a, b) => b.totalAttempts - a.totalAttempts).slice(0, 5).map(l => ({
          userId: l.userId,
          name: l.name,
          value: l.totalAttempts
        })),
        performers: [...learners].sort((a, b) => b.accuracy - a.accuracy).slice(0, 5).map(l => ({
          userId: l.userId,
          name: l.name,
          value: `${l.accuracy}%`
        })),
        improvers: [...learners].sort((a, b) => b.improvement - a.improvement).slice(0, 5).map(l => ({
          userId: l.userId,
          name: l.name,
          value: `${l.improvement >= 0 ? '+' : ''}${l.improvement}%`
        }))
      };

      byOrgId[org.id] = {
        learnersCount: learners.length,
        totalAttempts,
        accuracy: avgAccuracy,
        improvement: avgImprovement,
        leaderboards
      };
    });
    return byOrgId;
  }, [orgs, learnerStats]);
};

export default LeitnerEntity;
